import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, Loader2, Pencil } from "lucide-react";

import {
  parseLabelScanElementString,
  resolveGs1ManufacturerPartNumber,
  type LabelScanFields,
} from "@integrator-src/lib/gs1-element-string";
import {
  appendAltNumpadDigit,
  characterForCompletedAltNumpad,
} from "@integrator-src/lib/wedge-alt-numpad";
import { AmlManufacturerChoiceList } from "@/components/inventory/AmlManufacturerChoiceList";
import { CameraCaptureModal } from "@/components/ui/CameraCaptureModal";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FLOOR_TYPING_INPUT_PROPS, ensureFloorTypingInputKeyboard } from "@/lib/floorSerialScan";
import { useFloorPageScanCapture } from "@/lib/floorPageScanCapture";
import { useFloorTypingInputHandlers } from "@/lib/useFloorTypingInputKeyboard";
import {
  INBOUND_ARRIVAL_LABEL_SCAN_JPEG_QUALITY,
  INBOUND_ARRIVAL_LABEL_SCAN_MAX_LONG_EDGE,
  prepareInboundArrivalLabelPhoto,
} from "./inboundArrivalLabelPhoto";
import {
  fetchAmlManufacturerChoices,
  scanInboundMpnLabel,
  type InboundMpnLabelScanCandidate,
} from "./inboundApi";
import { parseInboundSupplierLabelQuantity } from "./inboundReceiveLabelScan";

function candidateLabel(field: InboundMpnLabelScanCandidate["field"]): string {
  switch (field) {
    case "manufacturer_part_number":
      return "Manufacturer part number";
    case "model":
      return "Model";
    case "part_number":
      return "Part number";
    case "customer_part_number":
      return "Customer part number";
    default:
      return "Label text";
  }
}

export function InboundReceiveMpnEntryModal(props: {
  open: boolean;
  sku: string;
  verifying?: boolean;
  onVerify: (mpn: string, manufacturerName: string | null, labelQuantity?: number | null) => void;
  /** When a label scan already has the part number, open straight on the manufacturer list. */
  seedMpn?: string | null;
  onClose: () => void;
}): JSX.Element | null {
  const inputRef = useRef<HTMLInputElement>(null);
  const altNumpadRef = useRef("");
  const pendingLabelQuantityRef = useRef<number | null>(null);
  const [mpnDraft, setMpnDraft] = useState("");
  const [labelFields, setLabelFields] = useState<LabelScanFields | null>(null);
  const [photoCandidates, setPhotoCandidates] = useState<InboundMpnLabelScanCandidate[]>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoScanning, setPhotoScanning] = useState(false);
  const [choiceLoading, setChoiceLoading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [manufacturerChoices, setManufacturerChoices] = useState<string[] | null>(null);
  const [pendingMpn, setPendingMpn] = useState("");
  const [choiceError, setChoiceError] = useState<string | null>(null);

  useEffect(() => {
    if (!props.open) {
      pendingLabelQuantityRef.current = null;
      setMpnDraft("");
      setLabelFields(null);
      setPhotoCandidates([]);
      setPhotoError(null);
      setPhotoScanning(false);
      setChoiceLoading(false);
      setCameraOpen(false);
      setManufacturerChoices(null);
      setPendingMpn("");
      setChoiceError(null);
    }
  }, [props.open, props.sku]);

  useEffect(() => {
    if (!props.open) {
      (window as Window & { __sgsReceiveMpnInputFocused?: boolean }).__sgsReceiveMpnInputFocused = false;
      return;
    }
    const focusInput = (): void => {
      const input = inputRef.current;
      if (input == null) return;
      ensureFloorTypingInputKeyboard(input);
      input.focus({ preventScroll: true });
      (window as Window & { __sgsReceiveMpnInputFocused?: boolean }).__sgsReceiveMpnInputFocused = true;
      if (input.value.trim() !== "") {
        input.select();
      }
    };
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(focusInput);
    });
    const retryTimer = window.setTimeout(focusInput, 80);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(retryTimer);
    };
  }, [props.open]);

  const typingInputHandlers = useFloorTypingInputHandlers();

  useFloorPageScanCapture({
    enabled: props.open,
    autoFocus: false,
    captureUnfocusedHid: true,
    inputRef,
    onScan: (value) => {
      if (props.verifying) return;
      applyScan(value);
    },
  });

  const beginManufacturerChoice = (trimmed: string, labelQuantity: number | null = null): void => {
    if (trimmed.length < 3 || props.verifying || choiceLoading) return;
    pendingLabelQuantityRef.current = labelQuantity;
    setMpnDraft(trimmed);
    setChoiceError(null);
    setChoiceLoading(true);
    void fetchAmlManufacturerChoices({ sku: props.sku, scanned_mpn: trimmed })
      .then((result) => {
        const resolvedMpn =
          "matched_mpn" in result && result.matched_mpn != null && result.matched_mpn.trim() !== ""
            ? result.matched_mpn.trim()
            : trimmed;
        setMpnDraft(resolvedMpn);
        if (result.status === "choose_manufacturer") {
          setPendingMpn(resolvedMpn);
          setManufacturerChoices(result.manufacturers);
          return;
        }
        setManufacturerChoices(null);
        const quantity = pendingLabelQuantityRef.current;
        pendingLabelQuantityRef.current = null;
        props.onVerify(resolvedMpn, null, quantity);
      })
      .catch((error: unknown) => {
        setChoiceError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        setChoiceLoading(false);
      });
  };

  const applyScan = (raw: string): void => {
    const trimmed = resolveGs1ManufacturerPartNumber(raw);
    if (trimmed.length < 3) return;
    setPhotoCandidates([]);
    setPhotoError(null);
    setLabelFields(parseLabelScanElementString(raw));
    beginManufacturerChoice(trimmed, parseInboundSupplierLabelQuantity(raw));
  };

  const applyCandidate = (candidate: InboundMpnLabelScanCandidate): void => {
    if (props.verifying || choiceLoading) return;
    setPhotoCandidates([]);
    setPhotoError(null);
    setLabelFields(null);
    beginManufacturerChoice(candidate.value, null);
  };

  const editCandidate = (candidate: InboundMpnLabelScanCandidate): void => {
    if (props.verifying || choiceLoading) return;
    setMpnDraft(candidate.value);
    setPhotoCandidates([]);
    setPhotoError(null);
    setLabelFields(null);
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (input == null) return;
      ensureFloorTypingInputKeyboard(input);
      input.focus({ preventScroll: true });
      input.select();
    });
  };

  const runVerify = (rawOverride?: string): void => {
    const source = rawOverride ?? mpnDraft;
    const trimmed = resolveGs1ManufacturerPartNumber(source);
    if (trimmed.length < 3) return;
    setPhotoCandidates([]);
    setPhotoError(null);
    setLabelFields(parseLabelScanElementString(source));
    beginManufacturerChoice(trimmed, parseInboundSupplierLabelQuantity(source));
  };

  useEffect(() => {
    if (!props.open) return;
    const seed = props.seedMpn?.trim() ?? "";
    if (seed.length < 3) return;
    beginManufacturerChoice(resolveGs1ManufacturerPartNumber(seed), null);
    // Seed is set by a label scan. Re-run only when that seed or the open flag changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.seedMpn]);

  const handleLabelPhoto = async (file: File | undefined): Promise<void> => {
    if (file == null || props.verifying) return;
    setPhotoScanning(true);
    setPhotoError(null);
    setPhotoCandidates([]);
    try {
      const prepared = await prepareInboundArrivalLabelPhoto(file);
      const result = await scanInboundMpnLabel(prepared);
      setPhotoCandidates(result.candidates);
      if (result.candidates.length === 1) {
        setMpnDraft(result.candidates[0]!.value);
      }
    } catch (error) {
      setPhotoError(error instanceof Error ? error.message : String(error));
    } finally {
      setPhotoScanning(false);
    }
  };

  if (!props.open) return null;

  const busy = props.verifying || photoScanning || choiceLoading;

  return createPortal(
    <>
      <div
        {...hubModalOverlayProps(true)}
      >
        <div
          className="mt-2 w-full max-w-md rounded-lg border border-slate-200 bg-white p-4 shadow-xl"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <h2 className="text-base font-semibold text-slate-900">
            {manufacturerChoices != null ? "Manufacturer" : "Manufacturer part number"}
          </h2>
          {manufacturerChoices != null ? (
            <div className="mt-3 space-y-3">
              <p className="font-mono text-sm text-slate-700">{pendingMpn}</p>
              <AmlManufacturerChoiceList
                manufacturers={manufacturerChoices}
                disabled={props.verifying}
                onChoose={(manufacturerName) => {
                  const quantity = pendingLabelQuantityRef.current;
                  pendingLabelQuantityRef.current = null;
                  props.onVerify(pendingMpn, manufacturerName, quantity);
                }}
              />
              {choiceError != null ? (
                <p className="text-sm text-rose-700" role="alert">{choiceError}</p>
              ) : null}
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-11 px-5 text-base touch-manipulation"
                  disabled={props.verifying}
                  onClick={props.onClose}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
          {manufacturerChoices == null ? (
          <div className="mt-3 space-y-3">
            <p className="text-base text-slate-600">
              Scan, type, or photograph the manufacturer part number for{" "}
              <span className="font-mono font-semibold text-slate-900">{props.sku}</span>.
            </p>
            <div>
              <label className="text-sm font-medium text-slate-700" htmlFor="inbound-receive-mpn-entry">
                MPN
              </label>
              <div className="mt-1 flex gap-2">
                <Input
                  id="inbound-receive-mpn-entry"
                  ref={inputRef}
                  value={mpnDraft}
                  className="min-h-12 font-mono text-base"
                  placeholder="Full MPN from reel or bag label"
                  disabled={busy}
                  {...FLOOR_TYPING_INPUT_PROPS}
                  {...typingInputHandlers}
                  onFocus={(event) => {
                    typingInputHandlers.onFocus(event);
                    (window as Window & { __sgsReceiveMpnInputFocused?: boolean }).__sgsReceiveMpnInputFocused = true;
                  }}
                  onBlur={() => {
                    (window as Window & { __sgsReceiveMpnInputFocused?: boolean }).__sgsReceiveMpnInputFocused = false;
                  }}
                  onChange={(event) => setMpnDraft(event.target.value)}
                  onKeyDown={(event) => {
                    const noted = appendAltNumpadDigit(altNumpadRef.current, event.code, event.altKey);
                    if (noted.consumed) {
                      altNumpadRef.current = noted.buffer;
                      event.preventDefault();
                      return;
                    }
                    if (event.key !== "Enter") return;
                    event.preventDefault();
                    runVerify(event.currentTarget.value);
                  }}
                  onKeyUp={(event) => {
                    if (event.key !== "Alt") return;
                    const separator = characterForCompletedAltNumpad(altNumpadRef.current);
                    altNumpadRef.current = "";
                    if (separator == null) return;
                    const input = event.currentTarget;
                    const start = input.selectionStart ?? input.value.length;
                    const end = input.selectionEnd ?? input.value.length;
                    setMpnDraft(input.value.slice(0, start) + separator + input.value.slice(end));
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-12 shrink-0 px-3 text-base touch-manipulation"
                  disabled={busy}
                  aria-label="Photograph label"
                  onClick={() => setCameraOpen(true)}
                >
                  {photoScanning ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <Camera className="h-4 w-4" aria-hidden />
                  )}
                </Button>
              </div>
              {labelFields != null && (labelFields.lot != null || labelFields.serial != null) ? (
                <p className="mt-1 text-xs text-slate-500">
                  {labelFields.lot != null ? `Lot ${labelFields.lot}` : null}
                  {labelFields.lot != null && labelFields.serial != null ? " · " : null}
                  {labelFields.serial != null ? `Serial ${labelFields.serial}` : null}
                </p>
              ) : null}
              {photoError != null || choiceError != null ? (
                <p className="mt-2 text-sm text-rose-700" role="alert">
                  {photoError ?? choiceError}
                </p>
              ) : null}
              {photoCandidates.length > 0 ? (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-medium text-slate-700">Select a part number from the photo</p>
                  <div className="flex flex-col gap-2">
                    {photoCandidates.map((candidate) => (
                      <div
                        key={`${candidate.field}:${candidate.value}`}
                        className="flex items-stretch gap-2"
                      >
                        <Button
                          type="button"
                          variant="secondary"
                          className="min-h-11 min-w-0 flex-1 justify-start px-3 text-left text-base touch-manipulation"
                          disabled={busy}
                          onClick={() => applyCandidate(candidate)}
                        >
                          <span className="font-mono font-semibold text-slate-900">{candidate.value}</span>
                          <span className="ml-2 text-xs text-slate-500">
                            {candidateLabel(candidate.field)}
                            {candidate.confidence !== "high" ? ` · ${candidate.confidence}` : ""}
                          </span>
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          className="min-h-11 shrink-0 px-3 touch-manipulation"
                          disabled={busy}
                          aria-label={`Edit ${candidate.value}`}
                          onClick={() => editCandidate(candidate)}
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">
                    Or edit the field above and tap Verify if none of these match.
                  </p>
                </div>
              ) : null}
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="primary"
                className="min-h-11 px-5 text-base touch-manipulation"
                disabled={mpnDraft.trim().length < 3 || busy}
                onClick={() => runVerify()}
              >
                {props.verifying || choiceLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  "Verify"
                )}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="min-h-11 px-5 text-base touch-manipulation"
                disabled={busy}
                onClick={props.onClose}
              >
                Cancel
              </Button>
            </div>
          </div>
          ) : null}
        </div>
      </div>
      <CameraCaptureModal
        open={cameraOpen}
        title="Component label"
        filenamePrefix="inbound-mpn-label"
        jpegQuality={INBOUND_ARRIVAL_LABEL_SCAN_JPEG_QUALITY}
        maxLongEdge={INBOUND_ARRIVAL_LABEL_SCAN_MAX_LONG_EDGE}
        onClose={() => setCameraOpen(false)}
        onCapture={(file) => {
          setCameraOpen(false);
          void handleLabelPhoto(file);
        }}
      />
    </>,
    document.body,
  );
}
